import Styles from '../../../1.manual/3.school/navigation/styles.module.css';
import LocalStyles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getFirestore, onSnapshot, query, where, writeBatch } from 'firebase/firestore';
import { jobPostingParentId, jobPostingYearDoc, writeJobPostingBothStores } from '../../jobPostingRef';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { applicationInfoData, defaultStoreData, employmentData20, employmentData18, employmentData17, formatStore, benefitData, infoData, summaryData, targetData } from 'src/type';
import { TbNumber1Small } from "react-icons/tb";
import { TbNumber2Small } from "react-icons/tb";
import { TbNumber3Small } from "react-icons/tb";
import { TbNumber4Small } from "react-icons/tb";
import { TbNumber5Small } from "react-icons/tb";
import { TbNumber6Small } from "react-icons/tb";
import { TbNumber7Small } from "react-icons/tb";
import { TbNumber8Small } from "react-icons/tb";
import { TbNumber9Small } from "react-icons/tb";
import { TbNumber10Small } from "react-icons/tb";
import { TbNumber11Small } from "react-icons/tb";
import { VscChevronRight } from "react-icons/vsc";
import { PiCheckCircleFill } from "react-icons/pi";
import { useAppSelector } from 'src/salon/app/hook';
import { registerJobPostingSearchIndex } from './searchIndexSync';

// 求人票の年度別編集画面(2.jobPostings/edit)専用のナビゲーション。
// 1.manual/3.school/navigation/index.tsx を元に、書き込み先を
// recruits/{companyId}_2027 のフラットドキュメント本体から
// recruits/{companyId}_2027/jobPostings/{year} のサブコレクションドキュメントに
// 変更している(パスの組み立ては ../../jobPostingRef に集約)。
// オンボーディング側(1.manual/3.school)には一切手を入れていない。
//
// 検索インデックス更新は searchIndexSync.ts の registerJobPostingSearchIndex
// を使う(旧 searchIndexRegister は _2027 決め打ちで年度別データに対応できない
// ため、別関数として新設した)。年度別サブコレクションにデータが複数年度分
// ある場合は、データが入っている最も新しい年度の内容を優先する。

const NAV_ITEMS = [
  { id: 1, label: "魅力", icon: <TbNumber1Small className={Styles.nav_number} /> },
  { id: 2, label: "企業情報", icon: <TbNumber2Small className={Styles.nav_number} /> },
  { id: 3, label: "店舗情報", icon: <TbNumber3Small className={Styles.nav_number} /> },
  { id: 4, label: "募集区分", icon: <TbNumber4Small className={Styles.nav_number} /> },
  { id: 5, label: "雇用条件(20歳卒)", icon: <TbNumber5Small className={Styles.nav_number} /> },
  { id: 6, label: "雇用条件(18歳卒)", icon: <TbNumber6Small className={Styles.nav_number} />, target: "高専(18歳卒)" },
  { id: 7, label: "雇用条件(17歳卒)", icon: <TbNumber7Small className={Styles.nav_number} />, target: "高専(17歳卒)" },
  { id: 8, label: "福利厚生", icon: <TbNumber8Small className={Styles.nav_number} /> },
  { id: 9, label: "採用情報", icon: <TbNumber9Small className={Styles.nav_number} /> },
  { id: 10, label: "パンフレット", icon: <TbNumber10Small className={Styles.nav_number} /> },
  { id: 11, label: "ハッシュタグ", icon: <TbNumber11Small className={Styles.nav_number} /> },
];

type SchoolNavigation = {
  summary: summaryData;
  info: infoData;
  target: targetData;
  benefit: benefitData;
  employment20: employmentData20;
  employment18: employmentData18;
  employment17: employmentData17;
  applicationInfo: applicationInfoData;
  progress: boolean[];
  companyId: string;
  year: string;
  changeData: boolean;
  navigation: number;
  setNavigation: React.Dispatch<React.SetStateAction<number>>;
  setBackCaution: React.Dispatch<React.SetStateAction<number>>;
  setConfirm: React.Dispatch<React.SetStateAction<boolean>>;
};

const SchoolNavigation: React.FC<SchoolNavigation> = ({
  target, employment20, employment18, employment17, progress, companyId, year, changeData, navigation, setNavigation, setBackCaution, setConfirm }) => {

  const company = useAppSelector((state) => state.company.company);

  const [ store, setStore ] = useState(defaultStoreData);
  const memoizedStore = useMemo(() => store, [ store ]);

  const db = getFirestore();

  useEffect(() => {
    const q = query(
      collection(db, 'stores'),
      where('companyId', '==', company.id),
      where('number', '==', 1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        const docData = docSnap.data();

        setStore({
          ...formatStore(docData),
          id: docSnap.id,
        });
      }
    }, (error) => {
      console.error('Firestore取得エラー:', error);
    });

    return () => unsubscribe(); // ←これ重要（メモリリーク防止）
  }, []);

  async function handleSame20to18() {
    await writeJobPostingBothStores(db, companyId, year, {
      employment18: employment20,
      'employment18.contents': "専門(20歳卒)の内容と同一",
    })
  }

  async function handleSame20to17() {
    await writeJobPostingBothStores(db, companyId, year, {
      employment17: employment20,
      'employment17.contents': "専門(20歳卒)の内容と同一",
    })
  }

  const publicDoc = async () => {

    setUploading(true);

    try {
      if (employment18.contents == "専門(20歳卒)の内容と同一") {
        await handleSame20to18();
      }

      if (employment17.contents == "専門(20歳卒)の内容と同一") {
        await handleSame20to17();
      }

      const docRef = jobPostingYearDoc(db, companyId, year);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("ドキュメントが存在しません");
      }

      // functions側(generateJobPostingPdfV2)で3パターン(20/18/17)分の
      // PDF生成・Storageアップロードまで行う。
      // 確定フラグより先に行い、PDFが作れなかったときは確定扱いにしない
      // (以前は先にフラグを立てていたため、PDF生成に失敗しても「確定済み」になっていた)。
      const functions = getFunctions();
      const generateJobPostingPdfV2Callable = httpsCallable(functions, "generateJobPostingPdfV2");
      await generateJobPostingPdfV2Callable({ companyId, year });

      // 年度ドキュメントとフラットドキュメントの確定フラグは1つのバッチでまとめて立てる。
      // フラットの jobPostingsStatus は「いずれかの年度の求人票を確定済み」の意味で、
      // バックエンドの一括ジョブ(functions/school-sending・aiScoringJob・
      // recruitRankingJob・aiRecruitAnalysisJob・recommendationCurrentJob)は
      // recruits コレクションを where('jobPostingsStatus','==',1) で絞り込むため、
      // 2028年度以降の確定でもフラットに立てる(これが無いと2028年度だけを持つ会社が
      // 学校への発送・AIスコア等から漏れる)。年度一覧(2.jobPostings/index.tsx)の
      // 2027年度の作成済み判定は、このフラグに加えてフラット側の employment20 も見ている。
      const statusBatch = writeBatch(db);
      statusBatch.set(jobPostingYearDoc(db, companyId, year), { jobPostingsStatus: 1 }, { merge: true });
      statusBatch.update(doc(db, 'recruits', jobPostingParentId(companyId)), { jobPostingsStatus: 1 });
      await statusBatch.commit();

      // ここまでで確定は完了。以降で失敗しても「確定に失敗」とは表示しない
      // (確定フラグとPDFはできているため、再度「確定」を押しても同じ状態にしかならない)。
      setConfirm(true);

      // 検索用データは確定フラグを読むため、フラグを立てた後に作る。
      // 一時的な通信エラーに備えて数回やり直し、それでも失敗したときは、確定は済んでいて
      // 学校・学生側の検索データだけが古い状態であることを伝える(運営側で作り直す)。
      let searchIndexError: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await registerJobPostingSearchIndex(companyId);
          searchIndexError = null;
          break;
        } catch (e) {
          searchIndexError = e;
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
      if (searchIndexError) {
        console.error("searchIndex更新エラー:", searchIndexError);
        alert("求人票の確定は完了しましたが、学校・学生向けの検索データの更新に失敗しました。お手数ですが運営までご連絡ください。");
      }

    } catch (error) {
      console.error("公開処理エラー:", error);
      alert("求人票の確定に失敗しました。時間をおいて、もう一度「確定」を押してください。");
      setConfirm(false);
    } finally {
      setUploading(false);
    }
  };

  const [ uploading, setUploading ] = useState(false);

  function checkProgress(progress: boolean[], target: string[]) {

    return progress.slice(0, 11).every((item, index) => {
      if (!target?.includes('高専(18歳卒)') && index === 5) return true;
      if (!target?.includes('高専(17歳卒)') && index === 6) return true;
      return Boolean(item);
    });
  }

  return (
    <>
    {uploading && (
      <div className={Styles.lockOverlay}>
        <div className={Styles.spinner} />
        <p>現在、求人票作成中...<br />（数分程度お時間がかかる場合がございます。）</p>
      </div>
    )}

    <div className={Styles.navigation}>
      <h4 className={`${Styles.navigation_title} ${LocalStyles.navigation_title}`}>求人票の編集</h4>

      {NAV_ITEMS.map(({ id, label, icon, target: targetCondition }) => {
        const isActive = navigation === id;
        const isDisabled = targetCondition && !target?.target?.includes(targetCondition);

        return (
          <div className={Styles.navigation_item}
            key={id} onClick={() => !isDisabled && (changeData && navigation !== id ? setBackCaution(id) : setNavigation(id))}
            style={{ backgroundColor: isActive ? "#EBEBEB" : "", cursor: isDisabled ? "not-allowed" : "pointer" }}>
            <p style={{ fontWeight: isActive ? "bold" : "", color: isDisabled ? "#CCCCCC" : "" }}>
              {icon} {label}
            </p>
            {progress[id - 1] ? (
              <PiCheckCircleFill className={Styles.save} />
            ) : (
              <VscChevronRight className={Styles.unSave} />
            )}
          </div>
        );
      })}

      <button className={Styles.save_btn}
        onClick={() => checkProgress(progress, target.target) ? publicDoc() : null}
        style={checkProgress(progress, target.target) ? {cursor: "pointer"} : { backgroundColor: '#fafafa', color: '#cccccc', border: '1px solid #EBEBEB', boxShadow: 'none', fontWeight: '400', }}>
          確定
      </button>

    </div>
    </>
  );
}

export default SchoolNavigation;
